import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { fetchCapacityDashboard } from './capacity.service'
import { CAPACITY_STATUS } from './capacity.constants'
import {
    getDefaultCapacityRange,
    getEmptyCapacitySummary,
    normalizeCapacityDashboardResult,
} from './capacity.utils'

const defaultRange = getDefaultCapacityRange()

const getApiError = (error, fallback) => {
    const data = error?.response?.data ?? {}
    const message = data.message ?? data.error ?? error?.message
    if (typeof message === 'string') return message
    if (typeof message === 'object' && typeof message.error === 'string') {
        return message.error
    }
    return fallback
}

const initialState = {
    status: 'idle',
    error: '',
    users: [],
    summary: getEmptyCapacitySummary(),
    search: '',
    projectIds: [],
    userIds: [],
    statusFilter: CAPACITY_STATUS.ALL,
    start: defaultRange.start,
    end: defaultRange.end,
}

export const fetchCapacityData = createAsyncThunk(
    'capacity/fetchData',
    async (_, { getState, rejectWithValue }) => {
        const { capacitySlice } = getState()

        try {
            const response = await fetchCapacityDashboard({
                search: capacitySlice.search,
                start: capacitySlice.start,
                end: capacitySlice.end,
                projectIds: capacitySlice.projectIds,
                userIds: capacitySlice.userIds,
                statusFilter: capacitySlice.statusFilter,
            })

            const body = response?.data ?? {}
            if (!body.success) {
                return rejectWithValue(
                    typeof body.message === 'string'
                        ? body.message
                        : 'Failed to load capacity data'
                )
            }

            return normalizeCapacityDashboardResult(body.result)
        } catch (error) {
            return rejectWithValue(
                getApiError(error, 'Failed to load capacity data')
            )
        }
    }
)

const capacitySlice = createSlice({
    name: 'capacitySlice',
    initialState,
    reducers: {
        setCapacityFilters: (state, action) => ({
            ...state,
            ...action.payload,
        }),
        resetCapacityFilters: (state) => ({
            ...state,
            search: '',
            projectIds: [],
            userIds: [],
            statusFilter: CAPACITY_STATUS.ALL,
            start: defaultRange.start,
            end: defaultRange.end,
        }),
        clearCapacityState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCapacityData.pending, (state) => {
                state.status = 'loading'
                state.error = ''
            })
            .addCase(fetchCapacityData.fulfilled, (state, action) => {
                state.status = 'loaded'
                state.users = action.payload?.users ?? []
                state.summary =
                    action.payload?.summary ?? getEmptyCapacitySummary()
            })
            .addCase(fetchCapacityData.rejected, (state, action) => {
                state.status = 'error'
                state.error =
                    action.payload || 'Failed to load capacity data'
                state.users = []
                state.summary = getEmptyCapacitySummary()
            })
    },
})

export const { setCapacityFilters, resetCapacityFilters, clearCapacityState } =
    capacitySlice.actions
export const capacityState = (state) => state.capacitySlice || initialState
export default capacitySlice.reducer
